import type {
  RunSelectorGroupingKey,
  WorkspacePageContent,
} from '../../store/thread';
import {agentPageContextChannel, type AgentPageContextReader} from '../channel';

/** ViewIdentity is the workspace view's identity, published as one value. */
type ViewIdentity = Pick<
  WorkspacePageContent,
  'viewId' | 'viewName' | 'viewType' | 'viewScope'
>;

export const viewIdentity = agentPageContextChannel<ViewIdentity>();
export const runSelectorGroupingKeys =
  agentPageContextChannel<RunSelectorGroupingKey[]>();
export const panelSearchQuery = agentPageContextChannel<string>();
export const runCountFiltered = agentPageContextChannel<number>();
export const runCountTotal = agentPageContextChannel<number>();
export const runCountListed = agentPageContextChannel<number>();
export const numRunsVisible = agentPageContextChannel<number>();
// numRunsHidden carries a count, or a note string when elided on large projects.
export const numRunsHidden = agentPageContextChannel<number | string>();
export const listOnlyVisibleRuns = agentPageContextChannel<boolean>();
export const runsFilter = agentPageContextChannel<string>();

/**
 * Run-visibility gating policy for large projects.
 *
 * On a project with at least LARGE_PROJECT_RUN_THRESHOLD runs, the exact
 * visible/hidden split is elided from agent page context — computing it would
 * require an uncached run-count aggregation over the whole project — and
 * LARGE_PROJECT_RUN_VISIBILITY_NOTE is sent as numRunsHidden in its place. See
 * WorkspacePageContent.numRunsHidden and useIsLargeProject.
 */
export const LARGE_PROJECT_RUN_THRESHOLD = 50_000;
export const LARGE_PROJECT_RUN_VISIBILITY_NOTE =
  'Large project - query for details if necessary';

/** workspaceReducer assembles the workspace page content from its channels. */
export const workspaceReducer = (
  read: AgentPageContextReader
): Omit<WorkspacePageContent, 'type'> => {
  return {
    ...read.one(viewIdentity),
    runCountTotal: read.one(runCountTotal),
    runCountFiltered: read.one(runCountFiltered),
    runCountListed: read.one(runCountListed),
    numRunsVisible: read.one(numRunsVisible),
    numRunsHidden: read.one(numRunsHidden),
    listOnlyVisibleRuns: read.one(listOnlyVisibleRuns) ?? undefined,
    runFiltersExpression: read.one(runsFilter),
    panelSearchQuery: read.one(panelSearchQuery),
    runSelectorGrouping: read.one(runSelectorGroupingKeys),
  };
};
